import React, { useState, useEffect } from 'react';

import '../reuse/formFieldChrome.css';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Used only to size the day list while the year is still unpicked, so Feb 29 stays selectable.
const LEAP_YEAR = 2024;

const daysInMonth = (monthName, year) => {
  const monthIndex = months.indexOf(monthName);
  if (monthIndex === -1) return 31;
  return new Date(year ? parseInt(year, 10) : LEAP_YEAR, monthIndex + 1, 0).getDate();
};

const DateSelector = ({ dateValue, onDateChange, disabledDate, dobErr, setDobErr }) => {
  const currentYear = new Date().getFullYear();
  const earliestYear = currentYear - 100;

  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  // Hydration only — this must never call onDateChange, so the parent can treat every
  // onDateChange as a user edit (that is what drives the auto-derived grade).
  useEffect(() => {
    const date = dateValue ? new Date(dateValue) : null;

    if (!date || isNaN(date.getTime())) {
      setDay('');
      setMonth('');
      setYear('');
      return;
    }

    setDay(date.getDate().toString());
    setMonth(months[date.getMonth()]);
    setYear(date.getFullYear().toString());
  }, [dateValue]);


  const updateParentDate = (y, m, d) => {
    setDobErr('');

    // Still filling the three parts in — not an error yet.
    if (!y || !m || !d) return;

    const monthIndex = months.indexOf(m);
    const dayInt = parseInt(d, 10);
    const yearInt = parseInt(y, 10);

    if (isNaN(yearInt)) {
      setDobErr('Please enter the year');
      return;
    }

    if (monthIndex === -1) {
      setDobErr('Invalid month selected.');
      return;
    }

    if (isNaN(dayInt) || dayInt < 1 || dayInt > 31) {
      setDobErr('Day must be between 1 and 31.');
      return;
    }

    const newDate = new Date(yearInt, monthIndex, dayInt);
    newDate.setHours(0, 0, 0, 0);

    if (newDate.getMonth() !== monthIndex) {
      setDobErr('The selected date does not exist.');
      return;
    }

    if (disabledDate(newDate)) {
      setDobErr('Not a valid Date of Birth');
      return;
    }

    onDateChange(newDate);
  };


  const applyChange = (nextDay, nextMonth, nextYear) => {
    let _day = nextDay;

    // A shorter month shouldn't strand an out-of-range day (Feb 30) on an error message.
    const lastDay = daysInMonth(nextMonth, nextYear);
    if (parseInt(_day, 10) > lastDay) {
      _day = lastDay.toString();
    }

    setDay(_day);
    setMonth(nextMonth);
    setYear(nextYear);
    updateParentDate(nextYear, nextMonth, _day);
  };


  const days = Array.from({ length: daysInMonth(month, year) }, (_, i) => (i + 1).toString());
  const yearOptions = Array.from({ length: currentYear - earliestYear + 1 }, (_, i) => `${currentYear - i}`);

  return (
    <div className="datePickerContainer">
      <div className="dobRow">
        <label className="dobField">
          <span className="fieldSubLabel">Day</span>
          <select
            className="form-control"
            value={day}
            onChange={e => applyChange(e.target.value, month, year)}>
            <option value="">Day</option>
            {days.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>

        <label className="dobField">
          <span className="fieldSubLabel">Month</span>
          <select
            className="form-control"
            value={month}
            onChange={e => applyChange(day, e.target.value, year)}>
            <option value="">Month</option>
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>

        <label className="dobField">
          <span className="fieldSubLabel">Year</span>
          <select
            className="form-control"
            value={year}
            onChange={e => applyChange(day, month, e.target.value)}>
            <option value="">Year</option>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      </div>

      {dobErr && (
        <div className="errorMsg" style={{ color: 'red' }}>
          {dobErr}
        </div>
      )}
    </div>
  );
};

export default DateSelector;
